import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { ApiWalletConfigService } from '../api/api-wallet-config.service';
import { ICutoffConfiguration, ICronExpressionOption } from '../../interfaces/wallet-config.interface';
import { COMMON_CRON_EXPRESSIONS, CUTOFF_CONFIG_ERROR_MESSAGES } from '../../constants/wallet-config.constants';

@Injectable({
  providedIn: 'root'
})
export class WalletConfigService {
  private selectedConfigurationSubject = new BehaviorSubject<ICutoffConfiguration | null>(null);
  public selectedConfiguration$ = this.selectedConfigurationSubject.asObservable();
  
  private selectedReportSubject = new BehaviorSubject<any>(null);
  public selectedReport$ = this.selectedReportSubject.asObservable();

  private configurationsSubject = new BehaviorSubject<ICutoffConfiguration[]>([]);
  public configurations$ = this.configurationsSubject.asObservable();

  constructor(private apiWalletConfigService: ApiWalletConfigService) {}

  // Establecer el reporte seleccionado para ver detalles
  setSelectedReport(report: any): void {
    this.selectedReportSubject.next(report);
  }

  // Obtener el reporte seleccionado actual
  getSelectedReport(): any {
    return this.selectedReportSubject.value;
  }

  // Limpiar selección
  clearSelection(): void {
    this.selectedReportSubject.next(null);
  }

  // Formatear el estado del reporte para mostrar
  formatReportStatus(status: string): { label: string; severity: string } {
    switch (status) {
      case 'success':
        return { label: 'Exitoso', severity: 'success' };
      case 'pending':
        return { label: 'Procesando', severity: 'warning' };
      case 'failed':
        return { label: 'Fallido', severity: 'danger' };
      default:
        return { label: 'Desconocido', severity: 'info' };
    }
  }

  // Validar formato de emails
  validateEmailList(emailString: string): boolean {
    const emails = emailString.split(',').map(email => email.trim());
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    return emails.every(email => emailRegex.test(email));
  }

  // Formatear tamaño de archivo
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Formatear tiempo promedio de procesamiento con validación mejorada
  formatProcessingTime(avgTime: string): string {
    if (!avgTime || avgTime === '0.0' || avgTime === '0' || avgTime === '') {
      return 'N/A';
    }
    
    // Asegurar que tenga formato de decimal
    const time = parseFloat(avgTime);
    if (isNaN(time) || time <= 0) {
      return 'N/A';
    }
    
    return `${time.toFixed(1)} min`;
  }

  // Formatear fecha del último reporte con validación mejorada
  formatLastReportDate(dateString: string): string {
    if (!dateString || dateString === '') {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }

  // Formatear tamaño promedio de archivo con validación
  formatAvgFileSize(sizeString: string): string {
    if (!sizeString || sizeString === 'N/A' || sizeString === '') {
      return 'N/A';
    }
    return sizeString;
  }

  // Procesar distribución por tipo de reporte
  processTypeDistribution(typeDistribution: Array<{_id: string, count: number}>): {
    automaticCount: number;
    manualCount: number;
    mostCommon: string;
    hasData: boolean;
  } {
    if (!typeDistribution || typeDistribution.length === 0) {
      return {
        automaticCount: 0,
        manualCount: 0,
        mostCommon: 'N/A',
        hasData: false
      };
    }

    const automaticCount = typeDistribution.find(item => item._id === 'automatic')?.count || 0;
    const manualCount = typeDistribution.find(item => item._id === 'manual')?.count || 0;
    
    let mostCommon = 'N/A';
    if (automaticCount > manualCount) {
      mostCommon = 'Automático';
    } else if (manualCount > automaticCount) {
      mostCommon = 'Manual';
    } else if (automaticCount > 0 && manualCount > 0) {
      mostCommon = 'Equilibrado';
    }

    return {
      automaticCount,
      manualCount,
      mostCommon,
      hasData: automaticCount > 0 || manualCount > 0
    };
  }

  // Validar datos de estadísticas
  validateStatisticsData(statistics: any): boolean {
    return statistics && 
           typeof statistics === 'object' &&
           (statistics.totalReports > 0 || 
            statistics.successfulReports > 0 || 
            statistics.failedReports > 0 ||
            statistics.currentMonthReports > 0);
  }

  // Obtener porcentaje de éxito
  getSuccessRate(successfulReports: number, totalReports: number): string {
    if (!totalReports || totalReports === 0) {
      return '0%';
    }
    
    const rate = (successfulReports / totalReports) * 100;
    return `${rate.toFixed(1)}%`;
  }

  // Obtener estadísticas resumidas para mostrar
  getStatisticsSummary(statistics: any): {
    hasData: boolean;
    totalReports: number;
    successfulReports: number;
    failedReports: number;
    successRate: string;
    currentMonthReports: number;
    avgProcessingTime: string;
    lastReportDate: string;
    avgFileSize: string;
    typeDistribution: any;
  } {
    if (!this.validateStatisticsData(statistics)) {
      return {
        hasData: false,
        totalReports: 0,
        successfulReports: 0,
        failedReports: 0,
        successRate: '0%',
        currentMonthReports: 0,
        avgProcessingTime: 'N/A',
        lastReportDate: 'N/A',
        avgFileSize: 'N/A',
        typeDistribution: { automaticCount: 0, manualCount: 0, mostCommon: 'N/A', hasData: false }
      };
    }

    const typeDistribution = this.processTypeDistribution(statistics.typeDistribution);

    return {
      hasData: true,
      totalReports: statistics.totalReports || 0,
      successfulReports: statistics.successfulReports || 0,
      failedReports: statistics.failedReports || 0,
      successRate: this.getSuccessRate(statistics.successfulReports || 0, statistics.totalReports || 0),
      currentMonthReports: statistics.currentMonthReports || 0,
      avgProcessingTime: this.formatProcessingTime(statistics.avgProcessingTime),
      lastReportDate: this.formatLastReportDate(statistics.lastReportDate),
      avgFileSize: this.formatAvgFileSize(statistics.avgFileSize),
      typeDistribution
    };
  }

  // Establecer la configuración seleccionada
  setSelectedConfiguration(configuration: ICutoffConfiguration | null): void {
    this.selectedConfigurationSubject.next(configuration);
  }

  // Obtener la configuración seleccionada actual
  getSelectedConfiguration(): ICutoffConfiguration | null {
    return this.selectedConfigurationSubject.value;
  }

  // Establecer la lista de configuraciones
  setConfigurations(configurations: ICutoffConfiguration[]): void {
    this.configurationsSubject.next(configurations);
  }

  // Obtener configuraciones actuales
  getConfigurations(): ICutoffConfiguration[] {
    return this.configurationsSubject.value;
  }

  // Formatear estado de configuración para mostrar
  formatConfigurationStatus(isActive: boolean): { label: string; severity: string } {
    return isActive 
      ? { label: 'Activo', severity: 'success' }
      : { label: 'Inactivo', severity: 'secondary' };
  }

  // Convertir expresión cron a texto legible en español
  cronToReadable(cronExpression: string): string {
    const parts = cronExpression.split(' ');
    if (parts.length !== 5) return cronExpression;

    const [minute, hour, day, month, dayOfWeek] = parts;

    // Casos especiales comunes
    if (cronExpression === '0 9 * * *') return 'Diario a las 9:00 AM';
    if (cronExpression === '0 9 1 * *') return 'Primer día del mes a las 9:00 AM';
    if (cronExpression === '57 16 L * *') return 'Último día del mes a las 4:57 PM';
    if (cronExpression === '0 8 * * 1') return 'Lunes a las 8:00 AM';
    if (cronExpression === '0 17 * * 5') return 'Viernes a las 5:00 PM';

    // Construcción básica
    let readable = '';

    // Frecuencia
    if (day === '*' && month === '*' && dayOfWeek === '*') {
      readable = 'Diariamente';
    } else if (day !== '*' && month === '*' && dayOfWeek === '*') {
      readable = `El día ${day} de cada mes`;
    } else if (day === '*' && month === '*' && dayOfWeek !== '*') {
      const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      readable = days[parseInt(dayOfWeek)] || `Día ${dayOfWeek} de la semana`;
    } else {
      readable = 'Programación personalizada';
    }

    // Hora
    if (minute === '0' && hour !== '*') {
      readable += ` a las ${hour}:00`;
    } else if (hour !== '*') {
      readable += ` a las ${hour}:${minute.padStart(2, '0')}`;
    }

    return readable;
  }

  // Obtener expresiones cron comunes
  getCommonCronExpressions(): ICronExpressionOption[] {
    return COMMON_CRON_EXPRESSIONS;
  }

  // Validar expresión cron
  validateCronExpression(cronExpression: string): boolean {
    const cronRegex = /^(\*|[0-5]?\d)\s+(\*|[01]?\d|2[0-3])\s+(\*|[012]?\d|3[01]|L)\s+(\*|[0-9]|1[0-2])\s+(\*|[0-6])$/;
    return cronRegex.test(cronExpression);
  }

  // Validar array de emails
  validateEmailArray(emails: string[]): boolean {
    if (!emails || emails.length === 0) return false;
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emails.every(email => emailRegex.test(email.trim()));
  }

  // Formatear lista de destinatarios para mostrar
  formatRecipientsList(recipients: string[], maxDisplay: number = 3): string {
    if (!recipients || recipients.length === 0) return 'Sin destinatarios';
    
    if (recipients.length <= maxDisplay) {
      return recipients.join(', ');
    }
    
    const displayed = recipients.slice(0, maxDisplay).join(', ');
    const remaining = recipients.length - maxDisplay;
    return `${displayed} y ${remaining} más`;
  }

  // Formatear cantidad de clientes filtrados
  formatClientCount(clientIds: string[]): string {
    if (!clientIds || clientIds.length === 0) return 'Todos los clientes';
    
    return `${clientIds.length} cliente${clientIds.length > 1 ? 's' : ''} específico${clientIds.length > 1 ? 's' : ''}`;
  }

  // Obtener mensaje de error apropiado
  getErrorMessage(errorCode: string): string {
    return (CUTOFF_CONFIG_ERROR_MESSAGES as any)[errorCode] || 'Error desconocido';
  }

  // Ordenar configuraciones (principal primero, luego por fecha de creación)
  sortConfigurations(configurations: ICutoffConfiguration[]): ICutoffConfiguration[] {
    return [...configurations].sort((a, b) => {
      // Configuración principal siempre primero
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      
      // Luego por fecha de creación (más reciente primero)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  // Limpiar todas las selecciones
  clearAllSelections(): void {
    this.selectedReportSubject.next(null);
    this.selectedConfigurationSubject.next(null);
  }

  // Calcular duración de procesamiento
  calculateProcessingDuration(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Menos de 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    return `${hours}h ${mins}min`;
  }
}